<template>
    <div class="space-y-5">
        <div>
            <h2 class="text-lg font-semibold text-gray-900">
                Experiences
            </h2>

            <p class="text-sm text-gray-500">
                Select the experiences included in this Lite Integration.
            </p>
        </div>

        <div
            v-if="error"
            class="rounded-md bg-red-50 p-3 text-sm text-red-700"
        >
            {{ error }}
        </div>

        <div class="rounded-lg border border-gray-200 bg-white p-4">
            <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div class="md:col-span-2">
                    <label class="mb-1 block text-sm font-medium text-gray-700">
                        Experience
                    </label>

                    <select
                        v-model="form.experience_uuid"
                        class="w-full rounded-md border border-gray-300 px-3 py-2"
                        :disabled="loadingExperiences || saving"
                    >
                        <option value="">
                            Select an experience
                        </option>

                        <option
                            v-for="experience in availableExperiences"
                            :key="experience.uuid"
                            :value="experience.uuid"
                        >
                            {{ experienceLabel(experience) }}
                        </option>
                    </select>
                </div>

                <div>
                    <label class="mb-1 block text-sm font-medium text-gray-700">
                        Roblox Integration ID
                    </label>

                    <input
                        v-model="form.roblox_integration_id"
                        type="number"
                        class="w-full rounded-md border border-gray-300 px-3 py-2"
                        placeholder="Optional"
                        :disabled="saving"
                    >
                </div>
            </div>

            <div
                v-if="validationErrors.experience_uuid"
                class="mt-2 text-sm text-red-600"
            >
                {{ validationErrors.experience_uuid[0] }}
            </div>

            <div class="mt-4 flex justify-end">
                <button
                    type="button"
                    class="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                    :disabled="!form.experience_uuid || saving"
                    @click="attachExperience"
                >
                    {{ saving ? 'Adding...' : 'Add Experience' }}
                </button>
            </div>
        </div>

        <div
            v-if="loadingAttached"
            class="py-8 text-center text-sm text-gray-500"
        >
            Loading attached experiences...
        </div>

        <div
            v-else-if="!attachedExperiences.length"
            class="rounded-lg border border-gray-200 bg-white p-8 text-center text-sm text-gray-500"
        >
            No experiences are attached.
        </div>

        <div
            v-else
            class="overflow-hidden rounded-lg border border-gray-200 bg-white"
        >
            <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                    <tr>
                        <th class="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                            Experience
                        </th>

                        <th class="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                            Place ID
                        </th>

                        <th class="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                            Roblox Integration ID
                        </th>

                        <th class="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500">
                            Actions
                        </th>
                    </tr>
                </thead>

                    <tbody class="divide-y divide-gray-200">
                        <template
                            v-for="relationship in attachedExperiences"
                            :key="relationship.uuid"
                        >
                            <tr>
                                <td class="px-4 py-3">
                                    <div class="flex items-center gap-3">
                                        <img
                                            v-if="relationship.experience.img_url"
                                            :src="relationship.experience.img_url"
                                            class="h-10 w-10 rounded-md object-cover"
                                        >

                                        <div>
                                            <div class="font-medium text-gray-900">
                                                {{
                                                    relationship.experience.friendly_name
                                                        || relationship.experience.name
                                                }}
                                            </div>

                                            <div
                                                v-if="
                                                    relationship.experience.friendly_name
                                                    && relationship.experience.name
                                                "
                                                class="text-xs text-gray-500"
                                            >
                                                {{ relationship.experience.name }}
                                            </div>
                                        </div>
                                    </div>
                                </td>

                                <td class="px-4 py-3 text-sm text-gray-600">
                                    {{ relationship.experience.place_id ?? '-' }}
                                </td>

                                <td class="px-4 py-3">
                                    <div class="flex items-center gap-2">
                                        <input
                                            v-model="relationship.roblox_integration_id"
                                            type="text"
                                            inputmode="numeric"
                                            class="w-56 rounded-md border border-gray-300 px-2 py-1.5 font-mono text-sm"
                                            placeholder="None"
                                            :disabled="updatingUuid === relationship.uuid"
                                            @keydown.enter.prevent="
                                                updateIntegrationId(relationship)
                                            "
                                        >

                                        <button
                                            type="button"
                                            class="rounded-md border border-blue-300 px-2 py-1.5 text-xs text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                                            :disabled="updatingUuid === relationship.uuid"
                                            @click="updateIntegrationId(relationship)"
                                        >
                                            {{
                                                updatingUuid === relationship.uuid
                                                    ? 'Saving...'
                                                    : 'Save'
                                            }}
                                        </button>

                                        <button
                                            type="button"
                                            class="rounded-md border border-gray-300 px-2 py-1.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                                            :disabled="updatingUuid === relationship.uuid"
                                            @click="resetIntegrationId(relationship)"
                                        >
                                            Reset to default
                                        </button>
                                    </div>
                                </td>

                                <td class="px-4 py-3 text-right">
                                    <button
                                        type="button"
                                        class="rounded-md border border-red-300 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                                        :disabled="
                                            deletingUuid === relationship.experience.uuid
                                        "
                                        @click="detachExperience(relationship)"
                                    >
                                        {{
                                            deletingUuid === relationship.experience.uuid
                                                ? 'Removing...'
                                                : 'Remove'
                                        }}
                                    </button>
                                </td>
                            </tr>

                            <tr>
                                <td
                                    colspan="4"
                                    class="bg-gray-50 px-4 py-4"
                                >
                                    <LiteIntegrationExperienceAssets
                                        :lite-integration="liteIntegration"
                                        :lite-integration-experience="relationship"
                                        :assets-revision="assetsRevision"
                                        :experience-assets-revision="experienceAssetsRevision"
                                        @experience-assets-changed="$emit('experience-assets-changed')"
                                    />
                                </td>
                            </tr>
                        </template>
                    </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import LiteIntegrationExperienceAssets from '@/components/LiteIntegrationExperienceAssets.vue';
const emptyForm = () => ({
    experience_uuid: '',
    roblox_integration_id: '',
});

export default {
    name: 'LiteIntegrationExperiences',

    components: {
        LiteIntegrationExperienceAssets,
    },

    props: {
        liteIntegration: {
            type: Object,
            required: true,
        },
        assetsRevision: {
            type: Number,
            required: true,
        },
        experienceAssetsRevision: {
            type: Number,
            required: true,
        },
    },

    data() {
        return {
            experiences: [],
            attachedExperiences: [],
            form: emptyForm(),
            validationErrors: {},
            loadingExperiences: false,
            loadingAttached: false,
            saving: false,
            deletingUuid: null,
            updatingUuid: null,
            error: null,
        };
    },

    computed: {
        attachedExperienceUuids() {
            return new Set(
                this.attachedExperiences.map(
                    relationship => relationship.experience.uuid
                )
            );
        },

        availableExperiences() {
            return this.experiences.filter(
                experience => !this.attachedExperienceUuids.has(experience.uuid)
            );
        },
    },
    
    watch: {
        experienceAssetsRevision() {
            this.loadAttachedExperiences();
        },
    },

    mounted() {
        this.load();
    },

    methods: {
        async load() {
            await Promise.all([
                this.loadExperiences(),
                this.loadAttachedExperiences(),
            ]);
        },

        async loadExperiences() {
            this.loadingExperiences = true;
            this.error = null;

            try {
                const response = await this.$http.get(
                    '/lite-integrations/experiences/available'
                );

                this.experiences = response.data.data ?? [];
            } catch (error) {
                this.error = this.getErrorMessage(
                    error,
                    'Unable to load experiences.'
                );
            } finally {
                this.loadingExperiences = false;
            }
        },

        async loadAttachedExperiences() {
            this.loadingAttached = true;
            this.error = null;

            try {
                const response = await this.$http.get(
                    `/lite-integrations/${this.liteIntegration.uuid}/experiences`
                );

                this.attachedExperiences = response.data.data ?? [];
            } catch (error) {
                this.error = this.getErrorMessage(
                    error,
                    'Unable to load attached experiences.'
                );
            } finally {
                this.loadingAttached = false;
            }
        },

        async attachExperience() {
            if (!this.form.experience_uuid) {
                return;
            }

            this.saving = true;
            this.error = null;
            this.validationErrors = {};

            try {
                await this.$http.post(
                    `/lite-integrations/${this.liteIntegration.uuid}/experiences`,
                    {
                        experience_uuid: this.form.experience_uuid,
                        roblox_integration_id:
                            this.form.roblox_integration_id || null,
                    }
                );

                this.form = emptyForm();

                await this.loadAttachedExperiences();
            } catch (error) {
                if (error.response?.status === 422) {
                    this.validationErrors =
                        error.response.data.errors ?? {};

                    return;
                }

                this.error = this.getErrorMessage(
                    error,
                    'Unable to attach experience.'
                );
            } finally {
                this.saving = false;
            }
        },

        async detachExperience(relationship) {
            const experience = relationship.experience;

            if (!window.confirm(
                `Remove "${experience.friendly_name || experience.name}" from this Lite Integration?`
            )) {
                return;
            }

            this.deletingUuid = experience.uuid;
            this.error = null;

            try {
                await this.$http.delete(
                    `/lite-integrations/${this.liteIntegration.uuid}/experiences/${experience.uuid}`
                );

                this.attachedExperiences =
                    this.attachedExperiences.filter(
                        item => item.experience.uuid !== experience.uuid
                    );
            } catch (error) {
                this.error = this.getErrorMessage(
                    error,
                    'Unable to remove experience.'
                );
            } finally {
                this.deletingUuid = null;
            }
        },

        experienceLabel(experience) {
            const name =
                experience.friendly_name
                || experience.name
                || experience.uuid;

            if (!experience.place_id) {
                return name;
            }

            return `${name} (${experience.place_id})`;
        },

        getErrorMessage(error, fallback) {
            return error.response?.data?.message ?? fallback;
        },
        
        async updateIntegrationId(relationship) {
            if (this.updatingUuid) {
                return;
            }

            this.updatingUuid = relationship.uuid;
            this.error = null;

            try {
                await this.$http.patch(
                    `/lite-integrations/${this.liteIntegration.uuid}`
                    + `/experiences/${relationship.uuid}`,
                    {
                        roblox_integration_id:
                            relationship.roblox_integration_id || null,
                    }
                );
            } catch (error) {
                this.error = this.getErrorMessage(
                    error,
                    'Unable to update the Roblox Integration ID.'
                );

                await this.loadAttachedExperiences();
            } finally {
                this.updatingUuid = null;
            }
        },

        async resetIntegrationId(relationship) {
            if (this.updatingUuid) {
                return;
            }

            const confirmed = window.confirm(
                'Reset this Roblox Integration ID to its default value?'
            );

            if (!confirmed) {
                return;
            }

            this.updatingUuid = relationship.uuid;
            this.error = null;

            try {
                const response = await this.$http.patch(
                    `/lite-integrations/${this.liteIntegration.uuid}`
                    + `/experiences/${relationship.uuid}`,
                    {
                        reset_roblox_integration_id: true,
                    }
                );

                relationship.roblox_integration_id =
                    response.data.data.roblox_integration_id;
            } catch (error) {
                this.error = this.getErrorMessage(
                    error,
                    'Unable to reset the Roblox Integration ID.'
                );
            } finally {
                this.updatingUuid = null;
            }
        },
    },
};
</script>